import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articleDetailMap } from '@/mocks/articles';
import { comments } from '@/mocks/homeData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const fallbackArticle = Object.values(articleDetailMap)[0];
  const article = slug ? (articleDetailMap[slug] || fallbackArticle) : fallbackArticle;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 pb-10">
          <div className="w-full px-4 md:px-6 text-center py-20">
            <p className="text-foreground-500 text-lg">文章未找到</p>
            <Link to="/" className="text-primary-500 hover:underline mt-4 inline-block">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${article.title} - 每日黑料`;
  const pageDescription = article.summary;

  return (
    <>
      <Navbar />
      <main>
        <article>
          <section className="pt-24 pb-8 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <Breadcrumb />

              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-xs font-medium text-primary-600 bg-primary-100/70 px-3 py-1 rounded-full"
                >
                  {article.category}
                </Link>
                <span className="text-xs text-foreground-500">{article.date}</span>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <p className="text-base md:text-lg text-foreground-600 mb-6 leading-relaxed">
                {article.summary}
              </p>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8">
                <span>作者：{article.author}</span>
                <span><i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万阅读</span>
                <span><i className="ri-message-2-line mr-1"></i>{article.comments}评论</span>
              </div>

              <div className="relative w-full h-[350px] md:h-[450px] rounded-2xl overflow-hidden mb-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Dramatic%20dark%20abstract%20artistic%20composition%20crimson%20red%20and%20black%20ink%20wash%20texture%20mysterious%20intense%20atmosphere%20cinematic%20lighting%20editorial%20journalism%20style%20wide%20panoramic%20format%20high%20contrast%20moody%20aesthetic&width=1400&height=700&seq=article-detail-hero&orientation=landscape"
                  alt={article.title}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </div>
          </section>

          <section className="py-10 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <div className="prose prose-lg max-w-none">
                {article.content.map((paragraph, index) => (
                  <p key={index} className="text-base md:text-lg text-foreground-800 leading-relaxed mb-6">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="whitespace-nowrap px-4 py-1.5 bg-secondary-100 text-secondary-700 rounded-full text-sm hover:bg-secondary-200 transition-colors"
                  >
                    #{article.category}
                  </Link>
                  <span className="whitespace-nowrap px-4 py-1.5 bg-background-100 text-foreground-600 rounded-full text-sm">
                    #每日黑料
                  </span>
                  <span className="whitespace-nowrap px-4 py-1.5 bg-background-100 text-foreground-600 rounded-full text-sm">
                    #吃瓜爆料
                  </span>
                </div>

                <div className="flex items-center gap-4 mt-6">
                  <button className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap">
                    <i className="ri-thumb-up-line"></i>点赞
                  </button>
                  <button className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap">
                    <i className="ri-share-forward-line"></i>分享
                  </button>
                  <button className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap">
                    <i className="ri-bookmark-line"></i>收藏
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">
                网友热评
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {comments.slice(0, 4).map((comment) => (
                  <div
                    key={comment.id}
                    className="bg-background-50 border border-background-200/70 rounded-xl p-5"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${
                            i < comment.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <h4 className="text-base font-bold text-foreground-950 mb-3">{comment.title}</h4>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{comment.content}</p>
                    <div className="flex items-center gap-3">
                      <img
                        src={comment.avatar}
                        alt={comment.user}
                        className="w-10 h-10 rounded-full object-cover"
                        loading="lazy"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{comment.user}</p>
                        <p className="text-xs text-foreground-500">{comment.date}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {Object.values(articleDetailMap)
                  .filter((a) => a.categorySlug === article.categorySlug && a.slug !== slug)
                  .slice(0, 2)
                  .map((related) => (
                    <Link
                      key={related.slug}
                      to="/category/all"
                      className="group bg-background-50 border border-background-200/70 rounded-xl p-5 hover:-translate-y-1 transition-all duration-300"
                    >
                      <span className="inline-block text-xs text-primary-600 bg-primary-100/70 px-2 py-0.5 rounded-full mb-2">
                        {related.category}
                      </span>
                      <h3 className="text-base font-heading font-bold text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors">
                        {related.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{related.summary}</p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span><i className="ri-eye-line mr-1"></i>{(related.views / 10000).toFixed(1)}万</span>
                        <span>{related.date}</span>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.summary,
            "datePublished": article.date,
            "author": { "@type": "Person", "name": article.author },
            "publisher": { "@type": "Organization", "name": "每日黑料", "url": "http://66012.cn/" },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://66012.cn/" },
              { "@type": "ListItem", "position": 2, "name": article.category, "item": `http://66012.cn/category/${article.categorySlug}` },
              { "@type": "ListItem", "position": 3, "name": article.title },
            ],
          }),
        }}
      />
    </>
  );
}